import { apiTransport } from './api-client';

/**
 * Reports, counted where the rows are.
 *
 * The reports screen used to build its charts by paging records into the
 * browser and adding them up — four thousand attendance records, five hundred
 * leave requests, five hundred employees. The API caps a page at two hundred.
 * So each chart was drawn from whatever fitted in one page and captioned as
 * the organisation's: not an error, not a blank screen, just a confidently
 * wrong number. That is the failure mode this file exists to remove.
 *
 * Every figure here is a `GROUP BY` on the server, over the whole set, within
 * the caller's own employee scope. A department head sees their department's
 * numbers because the server filters by scope, not because the screen asked
 * nicely.
 *
 * `insufficientData` comes back with each one and is not decoration. A
 * distribution over four people is disclosive as well as meaningless — it can
 * identify who was absent — and a chart that draws it anyway invites somebody
 * to read it.
 */

export interface WorkforceReport {
  headcount: number;
  byDepartment: { department: string; count: number }[];
  byStatus: { status: string; count: number }[];
  byType: { type: string; count: number }[];
  /** On the books with a last working day of today or later: "Separating". */
  separating?: number;
  insufficientData: boolean;
}

export interface AttendanceReport {
  period: { from: string; to: string };
  recordCount: number;
  /**
   * Day status to count — the status the Attendance list's Status column and
   * Status filter read, never the review queue.
   */
  byStatus: Record<string, number>;
  /** The Review filter's two states, counted apart from the outcomes. */
  byReview: { pending_review: number; explanation_requested: number };
  totalLateMinutes: number;
  totalUndertimeMinutes: number;
  flaggedCount: number;
  insufficientData: boolean;
}

export interface LeaveReport {
  byStatus: { status: string; count: number; days: string }[];
  /** Approved days only — a rejected request consumed nobody's balance. */
  byType: { leaveTypeId: string; name: string; count: number; days: string }[];
  insufficientData: boolean;
}

export const apiReportService = {
  workforce(): Promise<WorkforceReport> {
    return apiTransport().get<WorkforceReport>('/api/v1/reports/workforce');
  },

  attendance(from: string, to: string): Promise<AttendanceReport> {
    return apiTransport().get<AttendanceReport>('/api/v1/reports/attendance', {
      query: { from, to },
    });
  },

  leave(): Promise<LeaveReport> {
    return apiTransport().get<LeaveReport>('/api/v1/reports/leave');
  },
};
